import {OnboardingItemKey, OnboardingLockReason, OnboardingRole} from './core/_models'

const HELP_BASE = 'https://help.taskrush.com.br'

export type OnboardingItemMeta = {
  title: string
  benefit: string
  cta: string
  // Rota interna aberta ao clicar (o item manual é marcado no mesmo clique).
  to?: string
  // Link externo (post da Central de ajuda) aberto em nova aba.
  href?: string
  // Ação que abre um modal em vez de navegar.
  // `workday` abre o modal de jornada. `new-task` abre o modal de nova tarefa (o mesmo da barra superior).
  action?: 'workday' | 'new-task'
}

// Texto do checklist. Tom direto, sem travessão. Os links de ajuda só apontam para posts que
// existem hoje (mapa em modules/help/helpLinks.ts).
export const ONBOARDING_ITEMS: Record<OnboardingItemKey, OnboardingItemMeta> = {
  workday: {
    title: 'Definir seu custo por hora e sua jornada',
    benefit: 'Sem isso o TaskRush não calcula custo nem margem dos projetos.',
    cta: 'Definir',
    action: 'workday',
  },
  invite_team: {
    title: 'Convidar sua equipe',
    benefit: 'Cada pessoa lança a própria hora e vê as próprias tarefas.',
    cta: 'Convidar',
    to: '/users',
  },
  first_client: {
    title: 'Cadastrar o primeiro cliente',
    benefit: 'Projetos e contratos ficam ligados a um cliente.',
    cta: 'Cadastrar',
    to: '/clients?create=1',
  },
  first_project: {
    title: 'Criar o primeiro projeto',
    benefit: 'Comece do zero, de um modelo ou importando do Trello, ClickUp, Monday ou planilha.',
    cta: 'Criar projeto',
    to: '/projects?create=1',
  },
  first_task: {
    title: 'Criar a primeira tarefa',
    benefit: 'Registre o que precisa ser feito e quem vai fazer.',
    cta: 'Criar tarefa',
    action: 'new-task',
  },
  first_time_entry: {
    title: 'Lançar a primeira hora',
    benefit:
      'Abra uma tarefa e use o play para iniciar o timer. A hora é lançada quando você pausa.',
    cta: 'Ir para minhas tarefas',
    to: '/mytasks',
  },
  see_my_tasks: {
    title: 'Ver suas tarefas',
    benefit: 'Sua lista de trabalho do dia fica em Minhas tarefas.',
    cta: 'Abrir',
    to: '/mytasks',
  },
  see_team: {
    title: 'Ver como está a equipe',
    benefit: 'Desempenho, tarefas e horas de cada pessoa da equipe em um só lugar.',
    cta: 'Ver equipe',
    to: '/desempenho-equipe',
  },
  review_settings: {
    title: 'Conhecer as configurações da conta',
    benefit:
      'Defina regras da empresa: índice de lançamento de horas, margem mínima, quem pode excluir tarefas e mais.',
    cta: 'Abrir configurações',
    to: '/configs',
  },
  meet_rushmind: {
    title: 'Conhecer o RushMind',
    benefit: 'O assistente de IA destaca o que merece atenção e cobra o que está pendente.',
    cta: 'Saiba mais',
    href: `${HELP_BASE}/rushmind-taskrush-agente-de-ia/`,
  },
}

// Por que o item está bloqueado. Quem cadastra cliente e projeto é admin ou gestor, então o
// colaborador recebe uma explicação de que depende deles (e não um botão que não funcionaria).
export const getLockedText = (reason: OnboardingLockReason, role: OnboardingRole): string => {
  const canManage = role === 'admin' || role === 'manager'

  if (reason === 'needs_task') {
    return canManage
      ? 'Crie uma tarefa antes de lançar hora.'
      : 'Ainda não há tarefas na conta. Peça ao seu gestor para atribuir uma a você.'
  }

  if (canManage) {
    return reason === 'needs_client'
      ? 'Cadastre um cliente primeiro.'
      : 'Crie um projeto primeiro: toda tarefa pertence a um projeto.'
  }

  return 'Depende de um projeto cadastrado pelo seu gestor ou administrador. Enquanto isso, veja suas tarefas e conheça o RushMind.'
}
